import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  INITIAL_ZOOM_PAN,
  type ZoomPanAction,
  type ZoomPanSize,
  type ZoomPanState,
  zoomPanReducer,
} from './zoom-pan-reducer';

export interface UseZoomPanOptions {
  /** 그린 그림의 px 크기(렌더 결과). 아직 없으면 null. */
  readonly content: ZoomPanSize | null;
  /** 바뀌면 맞춤·가운데로 돌아간다 — 면 바꾸기(V10). */
  readonly resetKey: string;
}

type StagePointerHandler = (event: ReactPointerEvent<HTMLElement>) => void;

export interface ZoomPan {
  readonly state: ZoomPanState;
  /** 지금 배율(%) — 반올림. */
  readonly percent: number;
  readonly canZoomIn: boolean;
  readonly canZoomOut: boolean;
  /** 맞춤보다 크게 확대해 그림이 무대를 넘칠 때만 끌어서 움직인다. */
  readonly pannable: boolean;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly fit: () => void;
  /**
   * + = - 0 키(확대·축소·맞춤)와, 넘치게 확대했을 때의 화살표 키(이동). 뷰어 바깥 틀에 붙여 안쪽 어디에 포커스가 있어도 듣는다.
   * 그 밖의 키(Enter·Space·Tab 등)는 받지 않고 기본 동작도 막지 않는다.
   */
  readonly onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  /** 무대 요소 ref — 크기 재기와 Ctrl/⌘+휠(passive 아님)을 붙인다. */
  readonly stageRef: (element: HTMLElement | null) => void;
  readonly stageHandlers: {
    readonly onPointerDown: StagePointerHandler;
    readonly onPointerMove: StagePointerHandler;
    readonly onPointerUp: StagePointerHandler;
    readonly onPointerCancel: StagePointerHandler;
  };
  readonly stageStyle: CSSProperties;
  /** 그림(캔버스)에 붙인다 — 무대 가운데에 두고 CSS 배율·이동만 바꾼다(다시 그리지 않는다). */
  readonly contentStyle: CSSProperties;
}

const ZOOM_IN: ZoomPanAction = { type: 'zoomIn' };
const ZOOM_OUT: ZoomPanAction = { type: 'zoomOut' };
const FIT: ZoomPanAction = { type: 'fit' };
const KEY_ACTIONS = new Map<string, ZoomPanAction>([
  ['+', ZOOM_IN],
  ['=', ZOOM_IN],
  ['-', ZOOM_OUT],
  ['0', FIT],
]);
/**
 * 화살표 키 방향 — 스크롤처럼 그 방향을 더 보여 준다(→ 면 그림의 오른쪽이 보이도록 그림을 왼쪽으로 옮긴다).
 * 값은 무대 크기에 곱할 부호다.
 */
const ARROW_DIRECTIONS = new Map<string, { readonly x: number; readonly y: number }>([
  ['ArrowLeft', { x: 1, y: 0 }],
  ['ArrowRight', { x: -1, y: 0 }],
  ['ArrowUp', { x: 0, y: 1 }],
  ['ArrowDown', { x: 0, y: -1 }],
]);
/** 화살표 한 번에 옮기는 거리 — 무대 크기의 이 비율. */
const ARROW_PAN_RATIO = 0.1;
/** 휠 한 번 = 한 단계. 트랙패드처럼 잇달아 오는 휠은 이 간격에 한 번만 받는다. */
const WHEEL_STEP_INTERVAL_MS = 150;

/**
 * 확대·이동(V10) — 버튼·Ctrl/⌘+휠·키보드(+ = - 0, 화살표 이동)·끌기. 확대는 CSS 배율로만 하고 다시 그리지 않는다.
 * 무대 크기는 ResizeObserver 로 재고, resetKey(면)가 바뀌면 맞춤으로 돌아간다.
 */
export const useZoomPan = ({ content, resetKey }: UseZoomPanOptions): ZoomPan => {
  const [state, dispatch] = useReducer(zoomPanReducer, INITIAL_ZOOM_PAN);
  const [stage, setStage] = useState<HTMLElement | null>(null);
  const [viewport, setViewport] = useState<ZoomPanSize>({ width: 0, height: 0 });
  const drag = useRef<{
    readonly pointerId: number;
    readonly x: number;
    readonly y: number;
  } | null>(null);
  const contentWidth = content?.width ?? 0;
  const contentHeight = content?.height ?? 0;

  // 무대 크기 — ResizeObserver 가 없으면(jsdom) 붙을 때 한 번만 잰다.
  useEffect(() => {
    if (stage === null) return;
    const measure = (): void =>
      setViewport({ width: stage.clientWidth, height: stage.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [stage]);

  useEffect(() => {
    dispatch({ type: 'resize', viewport, content: { width: contentWidth, height: contentHeight } });
  }, [viewport, contentWidth, contentHeight]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey 가 바뀔 때만 맞춤으로 돌린다.
  useEffect(() => {
    dispatch(FIT);
  }, [resetKey]);

  // 브라우저 화면 확대 대신 그림을 확대한다 — React onWheel 은 passive 라 막을 수 없어 직접 붙인다.
  useEffect(() => {
    if (stage === null) return;
    let lastStep = Number.NEGATIVE_INFINITY;
    const onWheel = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const now = performance.now();
      if (event.deltaY === 0 || now - lastStep < WHEEL_STEP_INTERVAL_MS) return;
      lastStep = now;
      dispatch(event.deltaY < 0 ? ZOOM_IN : ZOOM_OUT);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [stage]);

  const pannable =
    state.level !== 'fit' &&
    (state.content.width * state.scale > state.viewport.width ||
      state.content.height * state.scale > state.viewport.height);
  const viewportWidth = state.viewport.width;
  const viewportHeight = state.viewport.height;

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      // Ctrl/⌘/Alt 조합은 브라우저·운영체제 단축키로 둔다.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const action = KEY_ACTIONS.get(event.key);
      if (action !== undefined) {
        event.preventDefault();
        dispatch(action);
        return;
      }
      const direction = ARROW_DIRECTIONS.get(event.key);
      // 옮길 곳이 없으면(맞춤·안 넘침) 화살표는 페이지·창 스크롤에 맡긴다.
      if (direction === undefined || !pannable) return;
      event.preventDefault();
      dispatch({
        type: 'pan',
        dx: direction.x * viewportWidth * ARROW_PAN_RATIO,
        dy: direction.y * viewportHeight * ARROW_PAN_RATIO,
      });
    },
    [pannable, viewportWidth, viewportHeight],
  );

  const endDrag: StagePointerHandler = (event) => {
    if (drag.current?.pointerId === event.pointerId) drag.current = null;
  };
  const stageHandlers: ZoomPan['stageHandlers'] = {
    onPointerDown: (event) => {
      if (!pannable || event.button !== 0) return;
      drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      const target = event.currentTarget;
      if (typeof target.setPointerCapture === 'function') target.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event) => {
      const current = drag.current;
      if (current === null || current.pointerId !== event.pointerId) return;
      drag.current = { pointerId: current.pointerId, x: event.clientX, y: event.clientY };
      dispatch({ type: 'pan', dx: event.clientX - current.x, dy: event.clientY - current.y });
    },
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  };

  const zoomIn = useCallback(() => dispatch(ZOOM_IN), []);
  const zoomOut = useCallback(() => dispatch(ZOOM_OUT), []);
  const fit = useCallback(() => dispatch(FIT), []);
  const stageRef = useCallback((element: HTMLElement | null) => setStage(element), []);

  return {
    state,
    percent: Math.round(state.scale * 100),
    canZoomIn: zoomPanReducer(state, ZOOM_IN) !== state,
    canZoomOut: zoomPanReducer(state, ZOOM_OUT) !== state,
    pannable,
    zoomIn,
    zoomOut,
    fit,
    onKeyDown,
    stageRef,
    stageHandlers,
    stageStyle: {
      position: 'relative',
      overflow: 'hidden',
      // 확대해 끌 때는 손가락 끌기를 페이지 스크롤이 아닌 이동으로 받는다(휴대폰).
      touchAction: pannable ? 'none' : 'manipulation',
      cursor: pannable ? 'grab' : 'default',
    },
    contentStyle: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: contentWidth,
      height: contentHeight,
      maxWidth: 'none',
      transformOrigin: 'center',
      transform: `translate(-50%, -50%) translate(${state.offsetX}px, ${state.offsetY}px) scale(${state.scale})`,
    },
  };
};
